import * as z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCustomError } from 'src/utils/custom-errors.hook'
import { useForm, SubmitHandler } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useCallback } from 'react'
import { CheckoutDocumentFormSchema, getCheckoutDocumentFormSchema } from './checkout-document-form.schema'

export default function useCheckoutDocumentForm(
  onSubmit: (data: CheckoutDocumentFormSchema, draft: boolean, finalized: boolean, updatePaymentDetailsOnly: boolean) => Promise<void>,
  defaultValues: Partial<CheckoutDocumentFormSchema>,
  unpaidPaymentActiveIds?: string[],
) {
  const { t } = useTranslation()

  const checkoutDocumentFormSchema = getCheckoutDocumentFormSchema(t, unpaidPaymentActiveIds)
  const customErrorMap = useCustomError()
  z.setErrorMap(customErrorMap)
  const {
    control,
    handleSubmit: rhfHandleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    getValues,
  } = useForm<CheckoutDocumentFormSchema>({ resolver: zodResolver(checkoutDocumentFormSchema), defaultValues: { ...defaultValues } })

  const submitHandler: (draft: boolean, finalized: boolean, updatePaymentDetailsOnly: boolean) => SubmitHandler<CheckoutDocumentFormSchema> = useCallback(
    (draft: boolean, finalized: boolean, updatePaymentDetailsOnly: boolean) => {
      return async (data: CheckoutDocumentFormSchema) => {
        await onSubmit(data, draft, finalized, updatePaymentDetailsOnly)
      }
    },
    [onSubmit],
  )

  const handleSubmit = useCallback(
    (draft: boolean, finalized: boolean, updatePaymentDetailsOnly: boolean) => rhfHandleSubmit(submitHandler(draft, finalized, updatePaymentDetailsOnly)),
    [rhfHandleSubmit, submitHandler],
  )

  return { handleSubmit, control, errors, reset, watch, setValue, getValues }
}
